/**
 * LibraryArticleCard — Compact, expandable card for the LitHub library.
 *
 * Layout mirrors the screening card design:
 * 1. Title + metadata (journal, specialty, pub date)
 * 2. Truncated abstract → expand/collapse
 * 3. Truncated AI summary → expand/collapse
 * 4. Action buttons: Audio Summary, Deep Dive, Discuss, Full Text (blue theme)
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useNavV2 } from '../hooks/useNavV2';
import { CrossAppLinks } from './CrossAppLinks';
import { SourcePermissionBadges } from './lithub/SourcePermissionBadges';
import { LitHubProvenancePanel } from './lithub/LitHubProvenancePanel';
import { resolveSourcePermissions } from '../utils/sourcePermissions';
import type { LibraryArticleRecord } from '../types/libraryProvenance';
import { notesAPI, articleMetadataAPI } from '../api';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  BookOpen,
  Sparkles,
  ExternalLink,
  Headphones,
  Brain,
  MessageSquare,
  FileText,
  Play,
  Loader2,
  RotateCcw,
  Trash2,
  CheckSquare,
  Square,
  StickyNote,
  Tag,
  FolderPlus,
  Save,
  X,
  Plus,
} from 'lucide-react';

const CARD_BG = [
  'bg-white',
  'bg-slate-50/70',
  'bg-gray-50/60',
  'bg-zinc-50/50',
];

const TRUNCATE_LEN = 220;

function truncateText(text: string | undefined, maxLen = TRUNCATE_LEN): { display: string; needsExpand: boolean } {
  if (!text || text.length <= maxLen) return { display: text || '', needsExpand: false };
  const cut = text.lastIndexOf(' ', maxLen);
  return { display: text.slice(0, cut > 0 ? cut : maxLen) + '...', needsExpand: true };
}

interface LibraryArticleCardProps {
  article: LibraryArticleRecord & { specialty_name?: string };
  index?: number;
  audioStatus?: string;
  audioUrl?: string | null;
  isSelected?: boolean;
  onToggleSelect?: (pmid: string) => void;
  onAudioSummary?: (pmid: string) => void;
  onPlayAudio?: (pmid: string, url: string) => void;
  onRetryAudio?: (pmid: string) => void;
  onDeepDive?: (pmid: string) => void;
  onDiscuss?: (pmid: string) => void;
  onSearchFullText?: (article: { pmid: string; title?: string }) => void;
  onRemove?: (pmid: string) => void;
  onAddToFolder?: (article: LibraryArticleRecord) => void;
  onViewInArchive?: () => void;
  onRead?: (pmid: string) => void;
  isRead?: boolean;
  collections?: Array<{ collection_id: string; name: string }>;
}

const LibraryArticleCard: React.FC<LibraryArticleCardProps> = ({
  article,
  index = 0,
  audioStatus = 'missing',
  audioUrl = null,
  isSelected = false,
  onToggleSelect,
  onAudioSummary,
  onPlayAudio,
  onRetryAudio,
  onDeepDive,
  onDiscuss,
  onSearchFullText,
  onRemove,
  onAddToFolder,
  onViewInArchive,
  onRead,
  isRead = false,
  collections = [],
}) => {
  const [abstractExpanded, setAbstractExpanded] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [inlinePlaying, setInlinePlaying] = useState(false);
  const navV2 = useNavV2();
  const permissions = resolveSourcePermissions(article);

  const shade = CARD_BG[index % CARD_BG.length];
  const pubmedUrl = `https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`;

  const hasAbstract = article.abstract &&
    article.abstract.length > 20 &&
    article.abstract.toLowerCase() !== 'no abstract available';

  const isSummaryUnavailable = !article.ai_summary ||
    article.ai_summary.length < 20 ||
    article.ai_summary.toLowerCase().includes('not available');

  const abstractInfo = truncateText(article.abstract);
  const summaryInfo = truncateText(article.ai_summary);

  const formattedDate = article.pub_date
    ? (() => {
        try {
          return new Date(article.pub_date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
          });
        } catch { return article.pub_date; }
      })()
    : null;

  return (
    <div
      className={`rounded-xl border transition-all shadow-sm hover:shadow-md ${shade} ${
        isSelected ? 'border-sky-400 ring-2 ring-sky-200/60' : isRead ? 'border-emerald-200/80' : 'border-slate-200/80'
      }`}
      data-testid={`library-card-${article.pmid}`}
    >
      {/* ─── Row 1: Title + Metadata ─── */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-start gap-3">
          {/* Selection checkbox */}
          {onToggleSelect && (
            <button
              className={`mt-0.5 shrink-0 transition-colors ${
                isSelected ? 'text-sky-600' : 'text-slate-300 hover:text-sky-400'
              }`}
              onClick={(e) => { e.stopPropagation(); onToggleSelect(article.pmid); }}
              data-testid={`select-${article.pmid}`}
            >
              {isSelected
                ? <CheckSquare className="h-5 w-5" />
                : <Square className="h-5 w-5" />}
            </button>
          )}

          <div className="flex-1 min-w-0">
            <a
              href={pubmedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="responsive-title text-base font-semibold text-slate-900 hover:text-sky-700 transition-colors leading-snug line-clamp-2 group"
              data-testid={`library-card-title-${article.pmid}`}
            >
              {article.title}
              <ExternalLink className="inline-block ml-1 h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity align-text-bottom" />
            </a>

            <div className="flex items-center gap-2 mt-1.5 flex-wrap text-sm">
              {article.journal && (
                <span className="font-medium text-sky-700">{article.journal}</span>
              )}
              {formattedDate && (
                <span className="text-slate-500">· {formattedDate}</span>
              )}
              {article.specialty_name && (
                <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700 px-1.5 py-0">
                  {article.specialty_name}
                </Badge>
              )}
              <span className="text-xs text-slate-400">PMID {article.pmid}</span>
            </div>
            <SourcePermissionBadges badges={permissions.badges} className="mt-2" />
          </div>
        </div>
      </div>

      <LitHubProvenancePanel article={article} onViewInArchive={onViewInArchive} />

      {/* ─── Row 2: Abstract ─── */}
      {hasAbstract && (
        <div className="px-5 py-2.5 border-t border-slate-100">
          <button
            onClick={() => { setAbstractExpanded(!abstractExpanded); if (!abstractExpanded && onRead) onRead(article.pmid); }}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
            data-testid={`toggle-abstract-${article.pmid}`}
          >
            <BookOpen className="h-3 w-3" /> Abstract
            {abstractInfo.needsExpand && (
              abstractExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {abstractExpanded ? article.abstract : abstractInfo.display}
          </p>
        </div>
      )}

      {/* ─── Row 3: AI Summary ─── */}
      {!isSummaryUnavailable && (
        <div className="px-5 py-2.5 border-t border-slate-100 bg-sky-50/30">
          <button
            onClick={() => { setSummaryExpanded(!summaryExpanded); if (!summaryExpanded && onRead) onRead(article.pmid); }}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sky-500 hover:text-sky-700 transition-colors"
            data-testid={`toggle-summary-${article.pmid}`}
          >
            <Sparkles className="h-3 w-3" /> AI Summary
            {summaryInfo.needsExpand && (
              summaryExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed">
            {summaryExpanded ? article.ai_summary : summaryInfo.display}
          </p>
        </div>
      )}

      {/* ─── Action Buttons ─── */}
      <div className="flex items-center gap-2 flex-wrap px-5 py-3 border-t border-slate-100">
        {audioStatus === 'pending' && (
          <Button
            variant="outline" size="sm" disabled
            className="h-8 px-3 text-xs text-sky-700 border-sky-200 bg-sky-50 animate-pulse"
            data-testid={`audio-btn-${article.pmid}`}
          >
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Generating...
          </Button>
        )}
        {audioStatus === 'ready' && (
          <Button
            variant="outline" size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (audioUrl) {
                setInlinePlaying((v) => !v);
                onPlayAudio?.(article.pmid, audioUrl);
              } else {
                onPlayAudio?.(article.pmid, '');
              }
            }}
            className="h-8 px-3 text-xs text-sky-700 border-sky-200 hover:bg-sky-50"
            data-testid={`audio-play-btn-${article.pmid}`}
          >
            <Play className="h-3.5 w-3.5 mr-1" />{inlinePlaying ? 'Hide Player' : 'Play Audio'}
          </Button>
        )}
        {audioStatus === 'failed' && (
          <Button
            variant="outline" size="sm"
            onClick={(e) => { e.stopPropagation(); onRetryAudio?.(article.pmid); }}
            className="h-8 px-3 text-xs text-rose-500 border-rose-200 hover:bg-rose-50"
            data-testid={`audio-retry-btn-${article.pmid}`}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" />Retry Audio
          </Button>
        )}
        {audioStatus === 'missing' && (
          <Button
            variant="outline" size="sm"
            onClick={(e) => { e.stopPropagation(); onAudioSummary?.(article.pmid); }}
            disabled={!permissions.canAudio}
            title={permissions.blockedAudioReason}
            className="h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50 disabled:opacity-50"
            data-testid={`audio-btn-${article.pmid}`}
          >
            <Headphones className="h-3.5 w-3.5 mr-1" />Audio Summary
          </Button>
        )}

        <Button
          variant="outline" size="sm"
          onClick={(e) => { e.stopPropagation(); onDeepDive?.(article.pmid); }}
          disabled={!permissions.canLitScholar}
          title={permissions.blockedLitScholarReason}
          className="h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50 disabled:opacity-50"
          data-testid={`deepdive-btn-${article.pmid}`}
        >
          <Brain className="h-3.5 w-3.5 mr-1" />Deep Dive with LitScholar
        </Button>

        {/* Discuss in LitClub */}
        <Button
          variant="outline" size="sm"
          onClick={(e) => { e.stopPropagation(); onDiscuss?.(article.pmid); }}
          className="h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
          data-testid={`discuss-btn-${article.pmid}`}
        >
          <MessageSquare className="h-3.5 w-3.5 mr-1" />Discuss in LitClub
        </Button>

        {onAddToFolder && (
          <Button
            variant="outline" size="sm"
            onClick={(e) => { e.stopPropagation(); onAddToFolder(article); }}
            className="h-8 px-3 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            data-testid={`folder-btn-${article.pmid}`}
          >
            <FolderPlus className="h-3.5 w-3.5 mr-1" />Save to folder
          </Button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Remove */}
        {onRemove && (
          <Button
            variant="ghost" size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(article.pmid); }}
            className="h-8 px-2 text-xs text-slate-400 hover:text-rose-500"
            data-testid={`remove-btn-${article.pmid}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Inline audio player — play directly on the card */}
        {audioStatus === 'ready' && audioUrl && inlinePlaying && (
          <div
            className="basis-full w-full mt-1"
            onClick={(e) => e.stopPropagation()}
            data-testid={`inline-audio-player-${article.pmid}`}
          >
            <audio controls autoPlay className="w-full h-9" src={audioUrl}>
              Your browser does not support audio.
            </audio>
          </div>
        )}
      </div>

      {/* Also in: other LitCurrent apps */}
      <CrossAppLinks
        pmid={article.pmid}
        profileId={navV2.profileId}
        originApp="learn"
        title={article.title}
        className="rounded-b-xl"
      />

      {/* Notes, Tags, Collections — flag-gated */}
      <ArticleMetadataSection pmid={article.pmid} collections={collections} />
    </div>
  );
};


/**
 * ArticleMetadataSection — Notes, Tags, Collections UI
 * Only renders when ENABLE_ARTICLE_NOTES flag is ON.
 * Self-contained: manages its own state and API calls.
 */
const ArticleMetadataSection: React.FC<{
  pmid: string;
  collections: Array<{ collection_id: string; name: string }>;
}> = ({ pmid, collections }) => {
  const enabled = useFeatureFlag('enable_article_notes');
  const [expanded, setExpanded] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<Array<{ note_id: string; body: string }>>([]);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Tags state
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Collections state
  const [articleCollectionIds, setArticleCollectionIds] = useState<string[]>([]);

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (enabled && expanded && !loaded) {
      loadAll();
    }
  }, [enabled, expanded, loaded]);

  const loadAll = async () => {
    try {
      const [notesData, tagsData, colsData] = await Promise.all([
        notesAPI.getNotesForArticle(pmid),
        articleMetadataAPI.getTags(pmid).catch(() => ({ tags: [] })),
        articleMetadataAPI.getArticleCollections(pmid).catch(() => ({ collection_ids: [] })),
      ]);
      setNotes(notesData || []);
      setTags(tagsData.tags || []);
      setArticleCollectionIds(colsData.collection_ids || []);
      setLoaded(true);
    } catch {}
  };

  if (!enabled) return null;

  return (
    <div className="border-t border-slate-100">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
        data-testid={`metadata-toggle-${pmid}`}
      >
        <StickyNote className="h-3 w-3" />
        Notes & Tags
        {notes.length > 0 && <Badge variant="secondary" className="h-4 px-1 text-[9px]">{notes.length}</Badge>}
        {tags.length > 0 && <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-violet-100 text-violet-600">{tags.length} tags</Badge>}
        {expanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-4" data-testid={`metadata-section-${pmid}`}>
          {/* Notes */}
          <div>
            <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Notes
            </h4>
            {notes.map(note => (
              <div key={note.note_id} className="mb-2 group">
                {editingNoteId === note.note_id ? (
                  <div className="flex gap-1">
                    <textarea
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      className="flex-1 text-xs border rounded p-2 min-h-[40px]"
                    />
                    <button onClick={async () => {
                      await notesAPI.updateNote(note.note_id, editValue);
                      setEditingNoteId(null);
                      setLoaded(false);
                    }} className="p-1 text-sky-600"><Save className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingNoteId(null)} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-slate-700 bg-amber-50/50 rounded p-2 border border-amber-100">
                    <span className="flex-1 whitespace-pre-wrap">{note.body}</span>
                    <button onClick={() => { setEditingNoteId(note.note_id); setEditValue(note.body); }}
                      className="p-0.5 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100"><StickyNote className="w-3 h-3" /></button>
                    <button onClick={async () => { await notesAPI.deleteNote(note.note_id); setLoaded(false); }}
                      className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-1">
              <input
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter' && newNote.trim()) {
                    await notesAPI.createNote(pmid, newNote.trim());
                    setNewNote('');
                    setLoaded(false);
                  }
                }}
                placeholder="Add a note..."
                className="flex-1 text-xs border rounded px-2 py-1.5 focus:ring-1 focus:ring-sky-300"
                data-testid={`note-input-${pmid}`}
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
              <Tag className="h-3 w-3" /> Tags
            </h4>
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 rounded-full">
                  {tag}
                  <button onClick={async () => {
                    const updated = tags.filter(t => t !== tag);
                    await articleMetadataAPI.setTags(pmid, updated);
                    setTags(updated);
                  }} className="hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
            <input
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && tagInput.trim()) {
                  const updated = [...tags, tagInput.trim()];
                  await articleMetadataAPI.setTags(pmid, updated);
                  setTags(updated);
                  setTagInput('');
                }
              }}
              placeholder="Add tag (Enter to save)..."
              className="text-xs border rounded px-2 py-1.5 w-full focus:ring-1 focus:ring-violet-300"
              data-testid={`tag-input-${pmid}`}
            />
          </div>

          {/* Collections */}
          {collections.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <FolderPlus className="h-3 w-3" /> Collections
              </h4>
              <div className="flex flex-wrap gap-1">
                {collections.map(col => {
                  const isIn = articleCollectionIds.includes(col.collection_id);
                  return (
                    <button
                      key={col.collection_id}
                      onClick={async () => {
                        if (isIn) {
                          await articleMetadataAPI.unassignFromCollection(col.collection_id, pmid);
                          setArticleCollectionIds(prev => prev.filter(id => id !== col.collection_id));
                        } else {
                          await articleMetadataAPI.assignToCollection(pmid, col.collection_id);
                          setArticleCollectionIds(prev => [...prev, col.collection_id]);
                        }
                      }}
                      className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                        isIn
                          ? 'bg-sky-100 border-sky-300 text-sky-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-sky-300'
                      }`}
                      data-testid={`collection-toggle-${col.collection_id}-${pmid}`}
                    >
                      {col.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LibraryArticleCard;
